import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, RefreshCcw, AlertTriangle } from "lucide-react";

/**
 * Live camera QR scanner using html5-qrcode.
 *  - Prefers the rear/environment-facing camera on mobile.
 *  - Emits `onResult(decodedText)` for the first successful decode per session,
 *    then pauses scanning until the parent calls `resetKey` (or unmounts).
 *  - Cleans up the camera stream on unmount.
 *
 * Props:
 *   onResult(text): callback with the decoded string
 *   active: boolean — whether the camera should be running
 *   onError(msg): optional callback when a fatal error occurs
 *   resetKey: any changing value that re-arms scanning after a decode
 */
export default function QrScanner({ onResult, active, onError, resetKey }) {
  const containerId = "pp-qr-reader";
  const scannerRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | starting | running | error
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [cameraId, setCameraId] = useState(null);
  const decodingLocked = useRef(false);

  // Discover cameras once
  useEffect(() => {
    if (!active) return;
    (async () => {
      try {
        const list = await Html5Qrcode.getCameras();
        setCameras(list || []);
        if (list?.length) {
          // Prefer rear/back camera on mobile
          const rear = list.find((c) => /back|rear|environment/i.test(c.label));
          setCameraId((rear || list[list.length - 1]).id);
        } else {
          setStatus("error");
          setError("No camera detected on this device.");
          onError?.("No camera detected on this device.");
        }
      } catch (e) {
        setStatus("error");
        const msg =
          e?.name === "NotAllowedError" || /permission|denied/i.test(String(e))
            ? "Camera permission was denied. Please allow camera access in your browser settings and try again."
            : "Couldn't access the camera. " + (e?.message || e);
        setError(msg);
        onError?.(msg);
      }
    })();
    // eslint-disable-next-line
  }, [active]);

  // Start / stop the scanner
  useEffect(() => {
    if (!active || !cameraId) return;
    let cancelled = false;
    (async () => {
      try {
        setStatus("starting");
        decodingLocked.current = false;
        const instance = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = instance;
        await instance.start(
          cameraId,
          {
            fps: 12,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const min = Math.min(viewfinderWidth, viewfinderHeight);
              const box = Math.floor(min * 0.72);
              return { width: box, height: box };
            },
            aspectRatio: 1.333,
            disableFlip: false,
          },
          (decodedText) => {
            if (decodingLocked.current) return;
            decodingLocked.current = true;
            onResult?.(decodedText);
          },
          () => { /* per-frame scan failures are noisy — ignore */ }
        );
        if (cancelled) {
          await instance.stop().catch(() => {});
          return;
        }
        setStatus("running");
      } catch (e) {
        setStatus("error");
        const msg =
          e?.name === "NotAllowedError" || /permission|denied/i.test(String(e))
            ? "Camera permission was denied. Please allow camera access in your browser settings and try again."
            : "Couldn't start the camera. " + (e?.message || e);
        setError(msg);
        onError?.(msg);
      }
    })();
    return () => {
      cancelled = true;
      const instance = scannerRef.current;
      if (instance) {
        instance.stop().catch(() => {}).finally(() => {
          try { instance.clear(); } catch { /* noop */ }
        });
        scannerRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [active, cameraId, resetKey]);

  // Full cleanup on unmount
  useEffect(() => () => {
    const instance = scannerRef.current;
    if (instance) {
      instance.stop().catch(() => {}).finally(() => {
        try { instance.clear(); } catch { /* noop */ }
      });
    }
  }, []);

  if (!active) return null;

  return (
    <div className="relative rounded-2xl overflow-hidden bg-black shadow-inner border border-brand-line" data-testid="qr-scanner-container">
      {/* Camera surface */}
      <div id={containerId} className="w-full aspect-square bg-black" />

      {/* Scan overlay (frame + corners) */}
      {status === "running" && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[72%] h-[72%]">
            <div className="absolute inset-0 rounded-2xl border-2 border-brand-yellow/40" />
            {/* Corner brackets */}
            {[
              "top-0 left-0 border-t-4 border-l-4 rounded-tl-2xl",
              "top-0 right-0 border-t-4 border-r-4 rounded-tr-2xl",
              "bottom-0 left-0 border-b-4 border-l-4 rounded-bl-2xl",
              "bottom-0 right-0 border-b-4 border-r-4 rounded-br-2xl",
            ].map((c, i) => (
              <div key={i} className={`absolute h-8 w-8 border-brand-yellow ${c}`} />
            ))}
            {/* Scanning line */}
            <div className="absolute left-2 right-2 top-0 h-0.5 bg-brand-yellow/80 shadow-[0_0_12px_2px_rgba(255,201,60,0.9)] animate-scan-line" />
          </div>
          <div className="absolute bottom-3 left-0 right-0 text-center text-xs text-white/85">Align the QR code within the frame</div>
        </div>
      )}

      {/* Status overlays */}
      {(status === "starting" || status === "idle") && (
        <div className="absolute inset-0 grid place-items-center text-white/80 text-sm">
          <div className="flex items-center gap-2"><Camera size={16} className="animate-pulse" /> Starting camera…</div>
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 grid place-items-center bg-black/70 p-6 text-center">
          <div>
            <AlertTriangle size={30} className="mx-auto text-brand-yellow" />
            <div className="mt-2 text-white font-medium">Camera unavailable</div>
            <div className="mt-1 text-white/70 text-sm max-w-xs" data-testid="qr-scanner-error">{error}</div>
          </div>
        </div>
      )}

      {/* Camera switch */}
      {cameras.length > 1 && status === "running" && (
        <div className="absolute top-3 right-3">
          <button
            data-testid="qr-scanner-switch-camera"
            onClick={() => {
              const idx = cameras.findIndex((c) => c.id === cameraId);
              const next = cameras[(idx + 1) % cameras.length];
              setCameraId(next.id);
            }}
            className="rounded-full bg-white/15 backdrop-blur-lg h-9 w-9 grid place-items-center text-white hover:bg-white/25"
            aria-label="Switch camera"
          >
            <RefreshCcw size={14} />
          </button>
        </div>
      )}

      <style>{`
        @keyframes ppScanLine { 0% { transform: translateY(0); } 100% { transform: translateY(calc(72vw * 0.72 - 4px)); } }
        .animate-scan-line { animation: ppScanLine 1.6s ease-in-out infinite alternate; }
      `}</style>
    </div>
  );
}

/** Non-camera fallback badge indicating status. */
export function ScannerIcon({ off }) {
  return off ? <CameraOff size={14} /> : <Camera size={14} />;
}
